export type ToolkitCallout = { type: "tip" | "warning" | "note"; text: string };

export type ToolkitSubsection = { title: string; body: string };

export type ToolkitShortcut = { keys: string; action: string };

export type ToolkitSection = {
  id: string;
  title: string;
  description: string;
  whyItMatters: string;
  howToUse: string[];
  proTips: string[];
  relatedIds: string[];
  subsections?: ToolkitSubsection[];
  callout?: ToolkitCallout;
  shortcuts?: ToolkitShortcut[];
  isOverview?: boolean;
  isFaq?: boolean;
};

export type ToolkitFaqItem = { id: string; question: string; answer: string };

export type ToolkitNavItem = { id: string; label: string };

export const TOOLKIT_PAGE_SUBTITLE =
  "Everything you need to design, review, test, collaborate on, and ship production-ready emails.";

export const TOOLKIT_NAV: ToolkitNavItem[] = [
  { id: "overview", label: "Overview" },
  { id: "getting-started", label: "Getting Started" },
  { id: "email-editor", label: "Email Editor" },
  { id: "components", label: "Components" },
  { id: "design-system", label: "Design System" },
  { id: "collaboration", label: "Collaboration" },
  { id: "comments", label: "Comments" },
  { id: "version-history", label: "Version History" },
  { id: "device-preview", label: "Device Preview" },
  { id: "dark-mode-preview", label: "Dark Mode Preview" },
  { id: "accessibility-checks", label: "Accessibility Checks" },
  { id: "preflight", label: "Preflight" },
  { id: "spam-checks", label: "Spam Checks" },
  { id: "link-validation", label: "Link Validation" },
  { id: "exporting", label: "Exporting" },
  { id: "templates", label: "Templates" },
  { id: "ai-assistance", label: "AI Assistance" },
  { id: "team-workspaces", label: "Team Workspaces" },
  { id: "keyboard-shortcuts", label: "Keyboard Shortcuts" },
  { id: "faq", label: "FAQ" },
];

function section(partial: ToolkitSection): ToolkitSection {
  return partial;
}

export const toolkitSections: ToolkitSection[] = [
  section({
    id: "overview",
    title: "Overview",
    isOverview: true,
    description:
      "Email Perfect is a visual email builder for teams who need design freedom and production-safe HTML. This toolkit explains what the product does, why each capability matters, and how to use it in a real email workflow.",
    whyItMatters:
      "Email production spans design, review, QA, and handoff. Scattered tools slow teams down and increase the risk of broken sends. Email Perfect keeps the full workflow in one place.",
    howToUse: [
      "Use the sidebar to jump to any topic.",
      "Search for a feature, step, or FAQ answer.",
      "Copy a section link to share with teammates.",
      "Follow related-feature links at the bottom of each section to explore connected workflows.",
    ],
    proTips: [
      "Start with Getting Started if you are new, then Preflight and Exporting before your first send.",
      "Bookmark deep links like /toolkit/preflight for quick reference.",
    ],
    relatedIds: ["getting-started", "preflight", "exporting"],
  }),
  section({
    id: "getting-started",
    title: "Getting Started",
    description:
      "Set up your account, create a project, invite collaborators, and choose whether to start from a template or a blank canvas.",
    whyItMatters:
      "A clear starting path helps new teams ship their first email faster and keeps permissions, assets, and projects organized from day one.",
    howToUse: [
      "Sign in with Google or a secure email link from the marketing site.",
      "Create or join a team from the Projects hub.",
      "Create a new project and pick a starting template—or start from scratch.",
      "Invite editors and viewers from Share when you are ready to collaborate.",
      "Open the editor and build your first section on the canvas.",
    ],
    proTips: [
      "Free teams can create up to three projects; upgrade when you need unlimited projects.",
      "Use viewer roles for stakeholders who should comment but not edit layout.",
    ],
    relatedIds: ["email-editor", "templates", "collaboration"],
  }),
  section({
    id: "email-editor",
    title: "Email Editor",
    description:
      "The canvas is where you compose emails visually—sections, columns, blocks, spacing, typography, and responsive behavior—without writing HTML by hand.",
    whyItMatters:
      "Email HTML is fragile across clients. A purpose-built editor lets you move fast visually while the engine handles compatibility constraints.",
    howToUse: [
      "Select a section on the canvas to edit layout, padding, and background.",
      "Add blocks from the Elements panel: text, images, buttons, and more.",
      "Use the Properties panel for typography, spacing, alignment, and responsive overrides.",
      "Switch between desktop and mobile views to tune stacking and alignment.",
      "Use breadcrumbs in the top bar to jump between template, section, and block scope.",
    ],
    proTips: [
      "Lock layout when you want to prevent accidental structural changes during review.",
      "Use mobile-specific alignment when a logo should center on phone but stay left on desktop.",
    ],
    relatedIds: ["components", "design-system", "device-preview"],
    subsections: [
      {
        title: "Canvas & layout",
        body: "Emails are built from sections containing columns and blocks. Drag to reorder, duplicate sections, and control container width and padding per section.",
      },
      {
        title: "Responsive settings",
        body: "Many properties support desktop and mobile values—stacking, padding, typography, and alignment can differ by breakpoint.",
      },
    ],
  }),
  section({
    id: "components",
    title: "Components",
    description:
      "Blocks and reusable pieces—text, images, buttons, dividers, spacers, social icons, and containers—each tuned for email-safe output.",
    whyItMatters:
      "Choosing the right block type keeps emails accessible, lightweight, and compatible. Reusable components scale design systems across campaigns.",
    howToUse: [
      "Open the Elements sidebar and drag a block into a column.",
      "Select the block to edit content and styles in Properties.",
      "Use spacers for vertical rhythm instead of empty paragraphs.",
      "Use buttons for primary CTAs; style them once and reuse via components on Plus and above.",
      "Use containers when you need grouped backgrounds or nested layout control.",
    ],
    proTips: [
      "Prefer hosted images over hotlinked URLs so thumbnails and preflight scans stay reliable.",
      "Social icon rows work well in footers; keep tap targets large enough for mobile.",
    ],
    relatedIds: ["design-system", "email-editor", "exporting"],
    callout: {
      type: "tip",
      text: "On Free plans you get Email Perfect elements. Plus unlocks custom components per project; Pro per team.",
    },
  }),
  section({
    id: "design-system",
    title: "Design System",
    description:
      "Global colors, typography presets, brand settings, and reusable styles keep campaigns on-brand across projects and teams.",
    whyItMatters:
      "Without shared tokens, every email reinvents colors and type. A lightweight design system speeds production and reduces inconsistency.",
    howToUse: [
      "Define brand colors and text styles at the template level.",
      "Apply presets to blocks instead of one-off hex values when possible.",
      "Use custom Google Fonts by pasting a Google Fonts URL in typography settings.",
      "Save repeated sections as components (Plus+) to propagate updates.",
    ],
    proTips: [
      "Pair custom fonts with sensible fallbacks—Email Perfect can suggest fallback stacks from your primary choice.",
      "Document your spacing rhythm (e.g. 8px grid) so editors make consistent choices.",
    ],
    relatedIds: ["components", "templates", "email-editor"],
  }),
  section({
    id: "collaboration",
    title: "Collaboration",
    description:
      "Work together in real time with shared editing, presence indicators, and team-scoped projects.",
    whyItMatters:
      "Email often involves designers, marketers, and stakeholders. Live collaboration cuts review cycles and version confusion.",
    howToUse: [
      "Share a project from the editor or Projects hub.",
      "Assign editor or viewer roles per teammate.",
      "Watch avatar stacks in the header to see who is in the file.",
      "Use comments for async feedback; editors resolve issues before export.",
    ],
    proTips: [
      "Viewers can follow along and comment without changing layout—ideal for legal or brand review.",
      "Editors consume seat limits per tier; plan roles before inviting a large group.",
    ],
    relatedIds: ["comments", "team-workspaces", "version-history"],
  }),
  section({
    id: "comments",
    title: "Comments",
    description:
      "Pin feedback to the canvas, reply in threads, and resolve comments as part of your review workflow.",
    whyItMatters:
      "Scattered Slack threads lose context. In-canvas comments tie feedback to the exact element being discussed.",
    howToUse: [
      "Toggle comment mode from the editor header.",
      "Click the canvas to place a comment anchor.",
      "Mention teammates by name in the comment body.",
      "Resolve comments when addressed; unresolved comments can block export on Free tier preflight.",
      "Replies require Plus or above.",
    ],
    proTips: [
      "Run Preflight before export to catch unresolved comments on Free plans.",
      "Use comments for copy changes, not structural experiments—edit directly when you own the layout.",
    ],
    relatedIds: ["collaboration", "preflight", "exporting"],
    callout: {
      type: "note",
      text: "Comment replies and history depth increase on higher tiers. See Pricing for role and history limits.",
    },
  }),
  section({
    id: "version-history",
    title: "Version History",
    description:
      "Snapshots capture meaningful states of your email so you can compare, restore, or recover from mistakes.",
    whyItMatters:
      "Campaigns iterate quickly. History gives you a safety net when someone overwrites a approved version.",
    howToUse: [
      "Open History from the editor when available on your plan.",
      "Browse snapshots with timestamps.",
      "Preview a snapshot before restoring.",
      "Restore a snapshot to replace the current canvas state.",
    ],
    proTips: [
      "Plus includes 10-step history; Pro includes 20-step; Enterprise is unlimited.",
      "Restore before major layout experiments so you can revert in one click.",
    ],
    relatedIds: ["collaboration", "exporting", "preflight"],
  }),
  section({
    id: "device-preview",
    title: "Device Preview",
    description:
      "Preview how your email reads on desktop and mobile widths before you send or export.",
    whyItMatters:
      "Most opens happen on mobile. Catching layout breaks early avoids embarrassing sends.",
    howToUse: [
      "Switch preview mode from the editor header.",
      "Compare desktop and mobile renderings side by side with your canvas settings.",
      "Verify stacking, font sizes, and button tap targets on narrow widths.",
      "Pair with Preflight for client-specific risk flags.",
    ],
    proTips: [
      "Adjust mobile-only alignment for logos and hero copy when desktop layout differs.",
      "Preview after changing section padding—mobile stacking magnifies spacing issues.",
    ],
    relatedIds: ["email-editor", "dark-mode-preview", "preflight"],
  }),
  section({
    id: "dark-mode-preview",
    title: "Dark Mode Preview",
    description:
      "Simulate how clients may invert or recolor your design in dark mode and spot contrast failures early.",
    whyItMatters:
      "Dark mode can flatten backgrounds, invert text, and break low-contrast designs. Previewing reduces surprises in Apple Mail, Gmail, and others.",
    howToUse: [
      "Open preview and enable dark mode simulation where available.",
      "Check logos, dividers, and button fills for sufficient contrast after inversion.",
      "Review Preflight dark-mode-related findings alongside the preview.",
      "Adjust colors or provide dark-mode-safe assets when warnings appear.",
    ],
    proTips: [
      "Avoid pure black and pure white pairs that clip in dark mode.",
      "Test hero images with transparent backgrounds—they often need a fallback fill.",
    ],
    relatedIds: ["accessibility-checks", "preflight", "device-preview"],
  }),
  section({
    id: "accessibility-checks",
    title: "Accessibility Checks",
    description:
      "Automated checks for color contrast, missing alt text, readable structure, and related WCAG-oriented issues.",
    whyItMatters:
      "Accessible emails reach more subscribers and reduce legal risk. Fixing issues in the builder is faster than patching HTML later.",
    howToUse: [
      "Add meaningful alt text on images in the Image block panel.",
      "Use sufficient contrast between text and background colors.",
      "Prefer semantic heading hierarchy in text blocks.",
      "Run Preflight to surface contrast and alt-text issues before export.",
    ],
    proTips: [
      "Image description (alt) is required for Plus+ preflight on non-placeholder images.",
      "WCAG contrast badges in the inspector show AA failures inline while you design.",
    ],
    relatedIds: ["preflight", "components", "exporting"],
  }),
  section({
    id: "preflight",
    title: "Preflight",
    description:
      "Catch rendering issues, accessibility concerns, missing assets, invalid links, and tier-specific show-stoppers before you export or send.",
    whyItMatters:
      "Most email problems are discovered after sending. Preflight helps identify issues before subscribers see them.",
    howToUse: [
      "Open your email in the editor.",
      "Open the Export panel and click Preflight—or toggle Export from the header.",
      "Click RUN CHECK and wait for the scan to complete (minimum ~3 seconds).",
      "Review show-stoppers (red), warnings (yellow), and alerts (blue).",
      "Click an issue to jump to the affected content when supported.",
      "Fix issues and re-run; export stays disabled until blockers from ran rules are cleared.",
    ],
    proTips: [
      "Editing the canvas marks results stale—re-run before every export.",
      "Free tier runs show-stoppers and unresolved comments only; Plus unlocks the full checklist.",
      "Monthly run limits apply by tier (25 / 50 / 100 / unlimited).",
    ],
    relatedIds: ["accessibility-checks", "dark-mode-preview", "link-validation", "exporting"],
    subsections: [
      {
        title: "Severity levels",
        body: "Show-stoppers block export. Warnings highlight risky patterns. Alerts flag informational items. Passing checks appear in a green PASSED section.",
      },
      {
        title: "Example findings",
        body: "Broken links, missing image descriptions (Plus+), low contrast, unresolved comments (Free), oversized assets, and dark-mode contrast risks.",
      },
      {
        title: "Stale state",
        body: "Any canvas edit after a run invalidates the last result until you RUN CHECK again. Download and Copy code stay disabled when stale or blocked.",
      },
    ],
    callout: {
      type: "warning",
      text: "Export actions (Download, Copy code) require a successful preflight run with no show-stoppers from rules that executed for your tier.",
    },
  }),
  section({
    id: "spam-checks",
    title: "Spam Checks",
    description:
      "Heuristic scans for subject lines, preview text, and body copy patterns that may hurt deliverability—available on Plus and above.",
    whyItMatters:
      "Spam filters react to shouting caps, excessive punctuation, and trigger phrases. Catching these before send protects reputation.",
    howToUse: [
      "Run Preflight on a Plus or Pro team.",
      "Review yellow spam warnings separately from red show-stoppers.",
      "Adjust subject and preview text when exclamation or ALL CAPS rules fire.",
      "Soften promotional language flagged in body heuristics.",
    ],
    proTips: [
      "Free teams see an upgrade banner for skipped spam checks—upgrade when deliverability matters for production sends.",
      "Pair spam warnings with a real seed-list test before major launches.",
    ],
    relatedIds: ["preflight", "ai-assistance", "exporting"],
  }),
  section({
    id: "link-validation",
    title: "Link Validation",
    description:
      "Validate hrefs, redirects, and tracking parameters so broken or malformed links do not reach subscribers.",
    whyItMatters:
      "A single broken CTA undermines trust and campaign metrics. Automated link checks catch 404s and malformed URLs early.",
    howToUse: [
      "Set button and text links in the editor with full URLs.",
      "Add UTM and custom tracking attributes where your analytics workflow requires them.",
      "Run Preflight to surface broken or empty links.",
      "Fix flagged links and re-run until link rules pass.",
    ],
    proTips: [
      "Use consistent UTM naming so reporting stays clean across campaigns.",
      "Link tracking limits vary by tier—see Pricing for caps on Free and Plus.",
    ],
    relatedIds: ["preflight", "exporting", "components"],
  }),
  section({
    id: "exporting",
    title: "Exporting",
    description:
      "Download production-ready HTML or copy code to the clipboard after preflight passes—optimized for ESP handoff.",
    whyItMatters:
      "The export is the deliverable. Clean, portable HTML reduces ESP-specific fixes and speeds time-to-send.",
    howToUse: [
      "Open the Export panel from the editor header.",
      "Run Preflight and clear show-stoppers.",
      "Click Download to save an HTML file or Copy code for your ESP.",
      "Paste into your sending platform and send a test email to your seed list.",
    ],
    proTips: [
      "Exports count against monthly tier limits on Free, Plus, and Pro.",
      "Re-run Preflight after any edit—export actions disable when results are stale.",
    ],
    relatedIds: ["preflight", "link-validation", "templates"],
  }),
  section({
    id: "templates",
    title: "Templates",
    description:
      "Start from Email Perfect templates, save your own starting points, and reuse layouts across projects.",
    whyItMatters:
      "Templates encode best practices—spacing, structure, and fallbacks—so teams do not rebuild common patterns.",
    howToUse: [
      "Pick a starter template when creating a project.",
      "Customize sections and save repeated layouts as components (tier-dependent).",
      "Duplicate projects to clone a proven campaign structure.",
    ],
    proTips: [
      "Keep a master template project for your house style and duplicate it per campaign.",
      "Document which template version was used for each send in your campaign tracker.",
    ],
    relatedIds: ["getting-started", "design-system", "components"],
  }),
  section({
    id: "ai-assistance",
    title: "AI Assistance",
    description:
      "AI credits power content suggestions, layout help, and writing assistance inside the editor workflow.",
    whyItMatters:
      "Drafting copy and structure faster accelerates iteration, especially for small teams without dedicated copywriters.",
    howToUse: [
      "Use AI actions from the editor where available.",
      "Generate or refine section copy, then edit visually on the canvas.",
      "Review AI output for brand voice before export.",
      "Monitor credit usage against your team tier allowance.",
    ],
    proTips: [
      "AI credits reset monthly by tier—see Pricing for allowances.",
      "Pair AI drafts with Preflight and accessibility checks before shipping.",
    ],
    relatedIds: ["email-editor", "components", "preflight"],
  }),
  section({
    id: "team-workspaces",
    title: "Team Workspaces",
    description:
      "Organizations, teams, roles, and shared assets structure how people and projects are managed at scale.",
    whyItMatters:
      "Clear ownership and permissions prevent accidental edits and make billing predictable across departments.",
    howToUse: [
      "Create teams from the Projects hub under your account.",
      "Assign owner, editor, and viewer roles per invite.",
      "Share components and libraries according to your tier (project, team, or org-wide on Enterprise).",
      "Enterprise customers can use workspaces to mirror org structure—contact sales for setup.",
    ],
    proTips: [
      "Workspaces are an Enterprise capability; most teams organize with projects and roles until then.",
      "Use separate teams for brands or regions when billing per team.",
    ],
    relatedIds: ["collaboration", "getting-started", "design-system"],
    callout: {
      type: "note",
      text: "SSO and org-wide workspace controls are Enterprise features. See the Enterprise callout on Pricing.",
    },
  }),
  section({
    id: "keyboard-shortcuts",
    title: "Keyboard Shortcuts",
    description:
      "Speed up editing with keyboard commands on the canvas. Shortcuts are disabled while typing in inputs.",
    whyItMatters:
      "Power users ship faster when common actions do not require menu hunting.",
    howToUse: [
      "Click the canvas background so focus is not inside a text field.",
      "Use the shortcuts below while editing (not in viewer role).",
    ],
    proTips: [
      "On Windows, use Ctrl instead of Cmd for the modifiers below.",
      "Undo history depth depends on your plan; keyboard undo respects the same stack.",
    ],
    relatedIds: ["email-editor", "components"],
    shortcuts: [
      { keys: "Cmd + Z", action: "Undo" },
      { keys: "Cmd + Shift + Z", action: "Redo" },
      { keys: "Ctrl + Y", action: "Redo (Windows)" },
      { keys: "Cmd + C", action: "Copy selected section or block" },
      { keys: "Cmd + L", action: "Copy link to selection" },
      { keys: "Cmd + V", action: "Paste below selection" },
      { keys: "Cmd + D", action: "Duplicate selected section or block" },
      { keys: "Delete / Backspace", action: "Delete selected section or block" },
    ],
  }),
];

export const toolkitFaqItems: ToolkitFaqItem[] = [
  {
    id: "faq-import-html",
    question: "Can I import existing HTML?",
    answer:
      "You can start from templates and build visually in the editor. Pasting raw HTML into the canvas is not the primary workflow—contact us if you need migration help for legacy templates.",
  },
  {
    id: "faq-coding",
    question: "Do I need coding knowledge?",
    answer:
      "No. Designers and marketers can build complete emails visually, run preflight, and export production HTML without writing code.",
  },
  {
    id: "faq-collaborators",
    question: "How many collaborators can edit?",
    answer:
      "Limits depend on your plan: Free includes 2 editors and 5 viewers; Plus includes 5 editors and 20 viewers; Pro and Enterprise offer unlimited roles. All roles count toward team collaboration limits.",
  },
  {
    id: "faq-clients",
    question: "Which email clients are supported?",
    answer:
      "Output targets broad compatibility including Gmail, Apple Mail, Yahoo, Outlook.com, and Outlook desktop with VML fallbacks where needed. Preflight and preview help catch client-specific risks.",
  },
  {
    id: "faq-dark-mode",
    question: "Does dark mode testing work automatically?",
    answer:
      "Dark mode preview and related preflight checks simulate common inversion behavior. Use them together before export—automatic guarantees for every client variant are not possible.",
  },
  {
    id: "faq-export-html",
    question: "Can I export raw HTML?",
    answer:
      "Yes. After preflight passes, download an HTML file or copy code from the Export panel for use in any ESP.",
  },
  {
    id: "faq-modify-code",
    question: "Does Email Perfect modify my code after export?",
    answer:
      "Export generates HTML from your design. Once exported, the file is yours—Email Perfect does not change ESP-hosted copies.",
  },
  {
    id: "faq-different",
    question: "How is this different from traditional email builders?",
    answer:
      "Email Perfect combines Figma-like visual freedom with email-safe output, preflight, collaboration, and tier-aware QA in one product.",
  },
  {
    id: "faq-responsive",
    question: "Can I make responsive emails?",
    answer:
      "Yes. Control stacking, spacing, typography, and alignment separately for mobile and desktop where properties support breakpoints.",
  },
  {
    id: "faq-tracking",
    question: "Can I track link clickthroughs?",
    answer:
      "Add UTM parameters and custom link attributes in the editor. Tier limits apply to how many tracking fields you can use per email.",
  },
  {
    id: "faq-preflight-limit",
    question: "How many preflight runs do I get?",
    answer:
      "Free: 25 per billing cycle. Plus: 50. Pro: 100. Enterprise: unlimited. Runs use the same anniversary cycle as test emails.",
  },
  {
    id: "faq-comments-free",
    question: "Can viewers leave comments?",
    answer:
      "Viewers can participate in review workflows. Replies and deeper comment history require Plus or above.",
  },
  {
    id: "faq-ai-credits",
    question: "How do AI credits work?",
    answer:
      "Each tier includes a monthly AI credit allowance. Credits are consumed when you run AI features; see Pricing for exact amounts.",
  },
  {
    id: "faq-sso",
    question: "Do you support SSO?",
    answer:
      "Single sign-on is available on Enterprise plans. Contact sales for security and provisioning details.",
  },
  {
    id: "faq-test-email",
    question: "Can I send test emails?",
    answer:
      "Yes. Send test messages from the editor subject to monthly tier limits, separate from preflight run counts.",
  },
  {
    id: "faq-outlook",
    question: "How does Outlook compatibility work?",
    answer:
      "The renderer emits VML and table-based fallbacks where Outlook desktop requires them—rounded buttons and backgrounds included.",
  },
];

export function getToolkitFeatureCount(): number {
  return toolkitSections.filter((s) => !s.isOverview && !s.isFaq).length;
}

export function estimateToolkitReadingMinutes(): number {
  const words = [
    ...toolkitSections.flatMap((s) => [
      s.title,
      s.description,
      s.whyItMatters,
      ...s.howToUse,
      ...s.proTips,
      ...(s.subsections?.flatMap((sub) => [sub.title, sub.body]) ?? []),
      ...(s.shortcuts?.flatMap((sh) => [sh.keys, sh.action]) ?? []),
    ]),
    ...toolkitFaqItems.flatMap((f) => [f.question, f.answer]),
  ]
    .join(" ")
    .split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 220));
}

export function getToolkitSectionById(id: string): ToolkitSection | undefined {
  return toolkitSections.find((s) => s.id === id);
}
